import React, { ReactNode, useRef } from "react";
import { copyToClipboard } from "fe-common/utils/copyToClipboard";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { styled } from "@mui/material";
import { LLMDisclaimer } from "fe-web-components/layout/LLMDisclaimer";
import { SummaryActionIcon } from "./summaryActionIcon";
import { SkeletonLoader } from "../loader/skeletonLoader";
import { PatientSummaryAction, SectionAction } from "./types/SectionAction";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { KAHUN_PLATFORM } from "@kahunmedical/user-management";
import { useCustomSummaryActions } from "./hooks/useCustomSummaryActions";
import { UsePatientSummaryApi } from "fe-common/patientSummary/useFetchPatientSummary";
import { AlertColor } from "@mui/material/Alert";
import ColumnHeader from "./ColumnHeader";

//region [[ Styles ]]

const InnerTextBox = styled("div")({
  //background: ClinicColors.textBoxBg,
  paddingBottom: 12,
  //borderRadius: 3,
  whiteSpace: "pre-wrap",
  //direction: "ltr",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientCardLlmTextProps {
  title?: string;
  children: string | ReactNode;
  errorText?: string;
  loading?: boolean;
  onShowMessage: (text: string | ReactNode, severity: AlertColor) => void;
  inExtension?: boolean;
  actions?: PatientSummaryAction[];
  clinicName?: string;
  clinicId?: string;
  platform?: KAHUN_PLATFORM;
  sectionName?: string;
  summaryApi?: UsePatientSummaryApi;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientCardLLMText = ({
  title,
  children,
  loading,
  errorText,
  onShowMessage,
  clinicName,
  platform,
  sectionName,
  clinicId,
  ...props
}: PatientCardLlmTextProps) => {
  const textElementRef = useRef(null);
  const { translate } = useTranslateByGender();
  const { mediatedActions } = useCustomSummaryActions({
    summaryApi: props.summaryApi,
    sectionActions: props.actions,
  });

  const handleActionClicked = (action: SectionAction, e: MouseEvent) => {
    evt({
      category: CATEGORY.PATIENT_CARD,
      action: ACTION.SECTION_ACTION_CLICKED,
      props: {
        clinicName,
        clinicId,
        platform,
        action: action.text,
        section: sectionName,
      },
    });
    action?.onClick?.(e, sectionName);
  };

  return (
    <>
      <ColumnHeader
        title={title}
        buttons={
          !loading &&
          !errorText && (
            <>
              <SummaryActionIcon
                noBackground
                onClick={() => {
                  copyToClipboard(textElementRef.current);
                  onShowMessage &&
                    onShowMessage(
                      `${title || ""} ${translate("COPIED")}`,
                      null,
                    );
                }}
                inExtension={props.inExtension}
              />
              {mediatedActions
                ?.filter((a) => a.section?.includes?.(sectionName))
                ?.map?.((action) => (
                  <SummaryActionIcon
                    key={action.text}
                    onClick={(e) => handleActionClicked(action, e)}
                    title={action.text}
                    icon={action.icon}
                    inExtension={props.inExtension}
                    noBackground
                  />
                ))}
              <LLMDisclaimer inExtension={props.inExtension} />
            </>
          )
        }
      />

      <InnerTextBox ref={textElementRef}>
        {loading ? (
          <SkeletonLoader />
        ) : errorText ? (
          <span className="error">{errorText}</span>
        ) : (
          children
        )}
      </InnerTextBox>
    </>
  );
};
